
import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingCTA from '../components/FloatingCTA';
import CookiePreferences from '../components/CookiePreferences';
import Button from '../components/Button';
import { Shield, Mail, Phone, Globe, Eye, Lock, FileText, UserCheck, Database, Settings } from 'lucide-react';
import { cookieManager, type ConsentData } from '../utils/cookieManager';

const Datenschutz = () => {
  const [showCookiePreferences, setShowCookiePreferences] = useState(false);

  const handleSavePreferences = (preferences: Omit<ConsentData, 'version' | 'timestamp' | 'expiryDate'>) => {
    cookieManager.setConsent(preferences);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="section-padding bg-primary-brand text-white pt-32">
        <div className="container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 fade-in-up">Datenschutzerklärung</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed fade-in-up-delay">
            Transparenz und Schutz Ihrer persönlichen Daten sind uns wichtig
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className="space-y-12 fade-in-up">
            
            {/* 1. Datenschutz auf einen Blick */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary-brand rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-primary-brand">1. Datenschutz auf einen Blick</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Allgemeine Hinweise</h3>
                  <p className="text-gray-700 mb-4">
                    Die folgenden Hinweise bieten dir einen schnellen Überblick darüber, was mit deinen personenbezogenen Daten passiert, wenn du unsere Website besuchst. Personenbezogene Daten sind alle Daten, mit denen du persönlich identifiziert werden kannst. Ausführliche Informationen findest du in der nachstehenden Datenschutzerklärung.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-primary-brand mb-3">Datenerfassung auf dieser Website</h4>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-gray-800 mb-2">Verantwortliche Stelle</h5>
                    <p className="text-sm text-gray-700">Die Datenverarbeitung erfolgt durch die AMMING GmbH (Kontaktdaten siehe Abschnitt „Hinweis zur verantwortlichen Stelle").</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-gray-800 mb-2">Art der Datenerhebung</h5>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>– Daten, die du uns aktiv mitteilst (z. B. über ein Kontaktformular).</li>
                      <li>– Daten, die beim Aufruf der Website automatisch erfasst werden (z. B. Browsertyp, Betriebssystem, Uhrzeit des Seitenaufrufs).</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-gray-800 mb-2">Zwecke der Verarbeitung</h5>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>– Technisch fehlerfreie Bereitstellung der Website.</li>
                      <li>– Analyse des Nutzerverhaltens.</li>
                      <li>– Bearbeitung (vor‑)vertraglicher Anfragen.</li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h5 className="font-medium text-blue-800 mb-2">Deine Rechte</h5>
                    <p className="text-sm text-blue-700">
                      Du hast jederzeit das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie ein Beschwerderecht bei der zuständigen Aufsichtsbehörde. Details findest du im Abschnitt „Deine Rechte".
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. Hosting */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary-brand rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-primary-brand">2. Hosting</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Externes Hosting</h3>
                  <p className="text-gray-700 mb-4">
                    Unsere Website wird von einem externen Dienstleister gehostet. Personenbezogene Daten, die auf dieser Website erfasst werden (z. B. IP‑Adresse, Kontaktanfragen, Meta‑ und Kommunikationsdaten), werden auf den Servern dieses Hosters verarbeitet.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-gray-800 mb-2">Rechtsgrundlagen:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>– Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung)</li>
                      <li>– Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer sicheren, schnellen und effizienten Bereitstellung)</li>
                      <li>– Art. 6 Abs. 1 lit. a DSGVO i. V. m. § 25 Abs. 1 TDDDG (Einwilligung, etwa für Cookies)</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-primary-brand pl-4 mb-4">
                    <h4 className="font-semibold text-primary-brand">Eingesetzter Hoster</h4>
                    <p className="text-gray-700">Vercel Inc., 340 S Lemon Ave #4133, Walnut, CA 91789, USA</p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-green-800 mb-2">Auftragsverarbeitung</h4>
                    <p className="text-sm text-green-700">
                      Mit Vercel haben wir einen Vertrag zur Auftragsverarbeitung abgeschlossen, der den datenschutzkonformen Umgang mit deinen Daten sicherstellt.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Weitere Dienstleister</h3>
                  
                  <div className="border-l-4 border-primary-brand pl-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Supabase (Datenbank‑ und Backend‑Service)</h4>
                    <ul className="text-sm text-gray-700 space-y-1 mb-2">
                      <li>– Supabase Inc., 440 N Barranca Ave #4133, Covina, CA 91723, USA</li>
                      <li>– Speicherung von Formular‑ und Kontaktdaten (Name, E‑Mail, Nachricht)</li>
                      <li>– Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)</li>
                      <li>– Garantien: EU‑Standardvertragsklauseln</li>
                    </ul>
                    <p className="text-sm text-gray-700">
                      Datenschutzerklärung: 
                      <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-brand hover:underline ml-1">
                        https://supabase.com/privacy
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. Allgemeine Hinweise und Pflichtinformationen */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary-brand rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-primary-brand">3. Allgemeine Hinweise und Pflichtinformationen</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Datenschutz</h3>
                  <p className="text-gray-700 mb-4">
                    Wir behandeln deine personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung. Bitte beachte, dass die Datenübertragung im Internet (z. B. E‑Mail) Sicherheitslücken aufweisen kann.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Hinweis zur verantwortlichen Stelle</h3>
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="font-semibold text-primary-brand mb-2">AMMING GmbH</h4>
                        <div className="space-y-1 text-sm text-gray-700">
                          <p>Max‑Planck‑Straße 4</p>
                          <p>85609 Aschheim</p>
                          <p>Deutschland</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-primary-brand" />
                          <a href="mailto:am@amming.de" className="text-primary-brand hover:underline text-sm">am@amming.de</a>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-primary-brand" />
                          <a href="tel:+4915770305796" className="text-primary-brand hover:underline text-sm">+49 1577 030 5796</a>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Globe className="h-4 w-4 text-primary-brand" />
                          <span className="text-sm text-gray-700">www.amming.de</span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                      <p><strong>Handelsregister:</strong> HRB 301840, Amtsgericht München</p>
                      <p><strong>Geschäftsführer (alleinvertretungsberechtigt):</strong> Alen Mikic</p>
                      <p><strong>Redaktionell verantwortlich:</strong> Alen Mikic</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Speicherdauer</h3>
                  <p className="text-gray-700">
                    Sofern in dieser Datenschutzerklärung keine speziellere Speicherdauer genannt ist, speichern wir personenbezogene Daten, bis der Verarbeitungszweck entfällt. Gesetzliche Aufbewahrungspflichten bleiben unberührt.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Rechtsgrundlagen unserer Datenverarbeitung</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm"><strong>Einwilligung:</strong> Art. 6 Abs. 1 lit. a DSGVO</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm"><strong>Vertrag/Vorvertrag:</strong> Art. 6 Abs. 1 lit. b DSGVO</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm"><strong>Rechtliche Verpflichtung:</strong> Art. 6 Abs. 1 lit. c DSGVO</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm"><strong>Berechtigtes Interesse:</strong> Art. 6 Abs. 1 lit. f DSGVO</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm"><strong>Besondere Kategorien:</strong> Art. 9 Abs. 2 lit. a DSGVO</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm"><strong>Drittstaatenübertragung:</strong> Art. 49 Abs. 1 lit. a DSGVO</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Widerruf deiner Einwilligung</h3>
                  <p className="text-gray-700">
                    Du kannst eine erteilte Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen. Die Rechtmäßigkeit der Verarbeitung bis zum Widerruf bleibt unberührt.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. Datenerfassung auf dieser Website */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary-brand rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-primary-brand">4. Datenerfassung auf dieser Website</h2>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Cookies</h3>
                  <p className="text-gray-700 mb-4">Unsere Website verwendet Cookies.</p>

                  <div className="space-y-4">
                    <div className="border-l-4 border-green-500 pl-4">
                      <h4 className="font-semibold text-green-800 mb-2">Technisch notwendige Cookies</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>– cookieConsent: speichert deine Zustimmung (Speicherdauer: 1 Jahr)</li>
                        <li>– cookiePreferences: speichert individuelle Präferenzen (Speicherdauer: 1 Jahr)</li>
                      </ul>
                      <p className="text-sm text-gray-600 mt-2">Diese Cookies setzen wir auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO.</p>
                    </div>

                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="font-semibold text-blue-800 mb-2">Analyse‑Cookies (nur mit Einwilligung)</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>– Google Analytics: Webanalyse; pseudonymisierte Nutzungs- und Gerätedaten; Serverstandort USA (EU‑SCC)</li>
                        <li>– Plausible Analytics: DSGVO‑konforme Webanalyse ohne Cookies; Serverstandort EU</li>
                      </ul>
                    </div>

                    <div className="border-l-4 border-purple-500 pl-4">
                      <h4 className="font-semibold text-purple-800 mb-2">Marketing‑Cookies (nur mit Einwilligung)</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>– Meta Pixel: Conversion‑Tracking und Zielgruppenbildung; pseudonymisierte Nutzungs- und Conversion‑Daten; Serverstandort USA (EU‑SCC)</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                    <p className="text-sm text-blue-700 mb-3">
                      Du kannst deine Cookie‑Einstellungen jederzeit über unseren Cookie‑Manager anpassen oder Cookies in deinem Browser löschen.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => setShowCookiePreferences(true)}
                      icon={<Settings className="w-4 h-4" />}
                      className="text-sm"
                    >
                      Cookie-Einstellungen verwalten
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Kontaktformular</h3>
                  <p className="text-gray-700 mb-4">
                    Wenn du uns über das Formular kontaktierst, speichern wir deine Angaben (Name, E‑Mail, Nachricht), um deine Anfrage zu bearbeiten.
                  </p>

                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-gray-800 mb-2">Rechtsgrundlagen:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li>– Art. 6 Abs. 1 lit. b DSGVO (vertraglicher Kontext)</li>
                      <li>– Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an effektiver Kommunikation)</li>
                      <li>– Art. 6 Abs. 1 lit. a DSGVO (Einwilligung, sofern erteilt)</li>
                    </ul>
                  </div>

                  <p className="text-sm text-gray-600">
                    Wir löschen die Daten, sobald der Zweck entfällt und keine gesetzlichen Aufbewahrungsfristen entgegenstehen.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-primary-brand mb-3">Anfrage per E‑Mail, Telefon oder Telefax</h3>
                  <p className="text-gray-700">
                    Auch hier verarbeiten wir deine Angaben zum Zweck der Bearbeitung deiner Anfrage. Rechtsgrundlagen entsprechen denen des Kontaktformulars.
                  </p>
                </div>
              </div>
            </div>

            {/* 5. Deine Rechte */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-primary-brand rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-primary-brand">5. Deine Rechte</h2>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-700">Du hast jederzeit das Recht auf:</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Auskunft</h4>
                    <p className="text-sm text-gray-700">über deine gespeicherten Daten</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Berichtigung</h4>
                    <p className="text-sm text-gray-700">falscher Daten</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Löschung</h4>
                    <p className="text-sm text-gray-700">oder Einschränkung der Verarbeitung</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Datenübertragbarkeit</h4>
                    <p className="text-sm text-gray-700">strukturierte Datenausgabe</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Widerspruch</h4>
                    <p className="text-sm text-gray-700">gegen bestimmte Verarbeitungen</p>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-primary-brand mb-2">Beschwerde</h4>
                    <p className="text-sm text-gray-700">bei der zuständigen Aufsichtsbehörde</p>
                  </div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <p className="text-sm text-blue-700">
                    Zudem kannst du erteilte Einwilligungen jederzeit widerrufen. Wende dich hierzu an die im Abschnitt „Hinweis zur verantwortlichen Stelle" genannten Kontaktdaten.
                  </p>
                </div>
              </div>
            </div>

            {/* Date */}
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 font-medium">Stand: 27. Mai 2025</p>
            </div>

          </div>
        </div>
      </section>

      <CookiePreferences
        isOpen={showCookiePreferences}
        onClose={() => setShowCookiePreferences(false)}
        onSave={handleSavePreferences}
      />

      <Footer />
      <FloatingCTA />
    </div>
  );
};

export default Datenschutz;
